import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAppData, updateAppDataWithRetry } from '@alchemiesolns/store-api';
import { PRODUCT_ID } from '../constants/product';
import { TRIAL_USAGE_LIMIT } from '../constants/trialUsage';

function normalizeCount(raw: unknown, fallback: number): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return fallback;
  return Math.max(0, Math.floor(raw));
}

export function useTrialUsage(opts: {
  uid: string | undefined;
  hasLicense: boolean;
  bypassBilling: boolean;
  authReady: boolean;
}) {
  const { uid, hasLicense, bypassBilling, authReady } = opts;
  const [consumed, setConsumed] = useState(0);
  const [trialLimit, setTrialLimit] = useState(TRIAL_USAGE_LIMIT);
  const [appDataSyncError, setAppDataSyncError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid || hasLicense || bypassBilling) {
      setConsumed(0);
      setTrialLimit(TRIAL_USAGE_LIMIT);
      setAppDataSyncError(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetchAppData(PRODUCT_ID);
        if (cancelled) return;
        const data = r.data ?? {};
        const limit = normalizeCount(data.trialUsageLimit, TRIAL_USAGE_LIMIT) || TRIAL_USAGE_LIMIT;
        const used = Math.min(limit, normalizeCount(data.trialUsageCount, 0));
        setTrialLimit(limit);
        setConsumed(used);
        setAppDataSyncError(null);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error && err.message.trim() ? err.message.trim() : 'Unknown appData sync error';
        console.warn('[trialUsage] /appData read failed; falling back to default trial state.', message);
        setAppDataSyncError(message);
        setTrialLimit(TRIAL_USAGE_LIMIT);
        setConsumed(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, hasLicense, bypassBilling]);

  const remaining = useMemo(() => {
    if (hasLicense || bypassBilling) return trialLimit;
    if (!uid) return 0;
    return Math.max(0, trialLimit - consumed);
  }, [uid, hasLicense, bypassBilling, consumed, trialLimit]);

  /**
   * Paid / dev bypass, or auth still loading (avoid flashing paywall), or trial uses left.
   */
  const trialAllowsAction =
    !authReady || bypassBilling || hasLicense || (!!uid && remaining > 0);

  /** Increments trial usage when applicable; returns remaining actions after consume, or `null` if skipped. */
  const consumeTrialUse = useCallback(async (): Promise<number | null> => {
    if (bypassBilling || hasLicense || !uid) return null;
    try {
      const result = await updateAppDataWithRetry(PRODUCT_ID, (current) => {
        const limit = normalizeCount(current.trialUsageLimit, TRIAL_USAGE_LIMIT) || TRIAL_USAGE_LIMIT;
        const used = Math.min(limit, normalizeCount(current.trialUsageCount, 0));
        const nextConsumed = Math.min(limit, used + 1);
        return {
          ...current,
          trialUsageLimit: limit,
          trialUsageCount: nextConsumed,
          lastSeenAt: new Date().toISOString(),
        };
      });
      const nextData = result.data ?? {};
      const nextLimit = normalizeCount(nextData.trialUsageLimit, TRIAL_USAGE_LIMIT) || TRIAL_USAGE_LIMIT;
      const nextConsumed = Math.min(nextLimit, normalizeCount(nextData.trialUsageCount, consumed + 1));
      setTrialLimit(nextLimit);
      setConsumed(nextConsumed);
      setAppDataSyncError(null);
      return Math.max(0, nextLimit - nextConsumed);
    } catch (err: unknown) {
      const message = err instanceof Error && err.message.trim() ? err.message.trim() : 'Unknown appData sync error';
      console.warn('[trialUsage] /appData write failed; using local in-memory fallback.', message);
      setAppDataSyncError(message);
      return Math.max(0, trialLimit - consumed);
    }
  }, [uid, hasLicense, bypassBilling, consumed, trialLimit]);

  return useMemo(
    () => ({
      trialLimit,
      consumed,
      remaining,
      trialAllowsAction,
      consumeTrialUse,
      appDataSyncError,
    }),
    [trialLimit, consumed, remaining, trialAllowsAction, consumeTrialUse, appDataSyncError],
  );
}
